import type { ReactElement, ReactNode } from "react";
import { useState } from "react";
import type { DroppableProvided, DropResult } from "react-beautiful-dnd";
import { DragDropContext, Droppable } from "react-beautiful-dnd";
import { ConstructionListRow } from "~/components/common/constructions/list/ConstructionListRow";
import type { NskResponse } from "~/models/Api";
import type { Deal } from "~/models/Deal";
import type {
	DealConstruction,
	DealConstructionContext,
	DealConstructionEdit
} from "~/models/DealConstruction";
import type { DealConstructionItem } from "~/models/DealConstructionItem";
import type { DealEstimationHeader } from "~/models/DealEstimationHeader";
import { ConstructionAmountRow } from "./ConstructionAmountRow";
import { ConstructionListFooterRow } from "./ConstructionListFooterRow";
import { FeeRow } from "./FeeRow";

type Props = {
	companyType: "client" | "vendor" | "partner";
	constructionContext: DealConstructionContext;
	deal: Deal;
	dealConstructionItems: DealConstructionItem[];
	docType: "billing" | "estimation";
	handleDestroy:
		| ((deleteConstruction: DealConstruction) => Promise<NskResponse>)
		| null;
	handleMutateConstructions: (() => Promise<void>) | null;
	handleUpdate:
		| ((
				submitValue: DealConstructionEdit,
				estimationId: number | null,
				historyId: number | null
		  ) => Promise<NskResponse>)
		| null;
	handleUpdateEnabled:
		| ((constructions: DealConstruction) => Promise<void>)
		| null;
	handleUpdateSeq:
		| ((constructions: DealConstruction[]) => Promise<void>)
		| null;
	onToggleSelect: ((id: number) => void) | null;
	readonly: boolean;
	requestHeader: DealEstimationHeader | null;
	selectedIds: readonly number[] | null;
	showEstimationConfirmedButton?: boolean;
	showHighlight: boolean;
	target: "owner" | "rentee" | null;
};

// TODO: 依存している react-beautiful-dnd は deprecated 済みのため `Sortable`（bb-ui）へ移行したい。
// ただし `Sortable` は固定行（FeeRow等）を差し込めないため、現状このtbodyには適用できない。
export function ConstructionListRows({
	readonly,
	deal,
	constructionContext,
	requestHeader,
	dealConstructionItems,
	handleMutateConstructions,
	handleUpdate,
	handleDestroy,
	handleUpdateSeq,
	handleUpdateEnabled,
	selectedIds,
	onToggleSelect,
	target,
	companyType,
	docType,
	showHighlight,
	showEstimationConfirmedButton = false
}: Props) {
	const { dealConstructions } = constructionContext;

	const [optimisticOrder, setOptimisticOrder] = useState<
		DealConstruction[] | null
	>(null);
	const orderedConstructions = optimisticOrder ?? dealConstructions;

	const renderRow = (
		dealConstruction: DealConstruction,
		index: number
	): ReactNode => (
		<ConstructionListRow
			deal={deal}
			dealConstruction={dealConstruction}
			dealConstructionItems={dealConstructionItems}
			docType={docType}
			handleDestroy={handleDestroy}
			handleMutateConstructions={handleMutateConstructions}
			handleUpdate={handleUpdate}
			handleUpdateEnabled={handleUpdateEnabled}
			handleUpdateSeq={handleUpdateSeq}
			index={index}
			key={dealConstruction.id}
			onToggleSelect={onToggleSelect}
			readonly={readonly}
			requestHeader={requestHeader}
			selectedIds={selectedIds}
			showHighlight={showHighlight}
			target={target}
		/>
	);
	const handleDragEnd = async (result: DropResult): Promise<void> => {
		const { destination, source } = result;
		if (!destination) {
			return;
		}
		if (
			destination.droppableId === source.droppableId &&
			destination.index === source.index
		) {
			return;
		}
		const reordered = [...orderedConstructions];
		const [movedConstruction] = reordered.splice(source.index, 1);
		reordered.splice(destination.index, 0, movedConstruction);
		const newConstructions = reordered.map(
			(construction: DealConstruction, index: number) => ({
				...construction,
				seq: index + 1
			})
		);
		setOptimisticOrder(newConstructions);
		try {
			await handleUpdateSeq?.(newConstructions);
			await handleMutateConstructions?.();
		} finally {
			setOptimisticOrder(null);
		}
	};
	const showFeeContext =
		constructionContext.type === "DealEstimationContext" ||
		constructionContext.type === "DealEstimationHistoryContext";
	const hasVendorFee = showFeeContext && constructionContext.vendorFee !== 0;
	return (
		<DragDropContext onDragEnd={handleDragEnd}>
			<Droppable droppableId="droppable">
				{(provided: DroppableProvided): ReactElement<HTMLElement> => (
					<tbody ref={provided.innerRef} {...provided.droppableProps}>
						{orderedConstructions.map(renderRow)}
						{provided.placeholder}
						{dealConstructions.length > 0 &&
						showFeeContext &&
						constructionContext.managementFee !== 0 ? (
							<FeeRow
								docType={docType}
								fee={constructionContext.managementFee}
								feeTitle="諸経費（工事管理費）"
								showButtons={!readonly}
								showSwitchEnable={handleUpdateEnabled !== null}
							/>
						) : null}
						{companyType === "vendor" && hasVendorFee ? (
							<ConstructionAmountRow
								constructionContext={constructionContext}
								docType={docType}
								showButtons={!readonly}
								showSwitchEnable={handleUpdateEnabled !== null}
							/>
						) : null}
						{dealConstructions.length > 0 &&
						companyType !== "client" &&
						showFeeContext &&
						hasVendorFee ? (
							<FeeRow
								docType={docType}
								fee={constructionContext.vendorFee}
								feeTitle="うち送客手数料"
								showButtons={!readonly}
								showSwitchEnable={handleUpdateEnabled !== null}
							/>
						) : null}
						<ConstructionListFooterRow
							companyType={companyType}
							constructionContext={constructionContext}
							docType={docType}
							hasVendorFee={hasVendorFee}
							showButtons={!readonly}
							showEstimationConfirmedButton={showEstimationConfirmedButton}
							showSelectColumn={selectedIds !== null}
							showSwitchEnable={handleUpdateEnabled !== null}
							target={target}
						/>
					</tbody>
				)}
			</Droppable>
		</DragDropContext>
	);
}
